import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, Step, StepLabel, Stepper, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Alert, AlertTitle } from '@material-ui/lab';
import { logEvent } from 'components/telemetry';
import {
	BulkChangePlan,
	describeDuration,
	estimateDurationMs,
	invertPlan,
	isUndoable,
	planToUndoWrites,
	planToWrites,
	selectableRows,
} from 'helpers/bulkEdit/changePlan';
import { BulkRunProgress, BulkRunResult, runBulkWrites } from 'helpers/bulkEdit/runEngine';
import { parseCsv, rowsToObjects } from 'helpers/csv';
import React from 'react';
import { BulkEditContext } from './BulkEditDialog';
import { RunProgressView, RunResultAlert } from './BulkRunView';
import { buildImportPlan, CARD_COLUMN, editableColumns, ID_COLUMN, ImportMissingAction, ImportParseResult } from './importPlan';
import PreviewTable, { RowRunStatus } from './PreviewTable';

const DIALOG_Z_INDEX = 10050;
const OVERLAY_Z_INDEX = DIALOG_Z_INDEX + 10;
const overlayProps = { style: { zIndex: OVERLAY_Z_INDEX } };

const STEPS = ['Choose file', 'Review', 'Apply'];

type WizardStep = 'choose' | 'preview' | 'run' | 'result';

interface Props {
	open: boolean;
	onClose: () => void;
	getContext: () => BulkEditContext;
	onApplied: () => void;
}

const useStyles = makeStyles((theme) => ({
	section: { marginBottom: theme.spacing(2) },
	counts: { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: theme.spacing(1) },
	table: { border: '1px solid rgba(0, 0, 0, 0.12)', borderRadius: 4 },
	fileName: { marginLeft: theme.spacing(1) },
}));

export function ImportDialog({ open, onClose, getContext, onApplied }: Props) {
	let classes = useStyles();

	let [context, setContext] = React.useState<BulkEditContext | null>(null);
	let [step, setStep] = React.useState<WizardStep>('choose');
	let [fileName, setFileName] = React.useState('');
	let [fileText, setFileText] = React.useState('');
	let [missingAction, setMissingAction] = React.useState<ImportMissingAction>('leave');
	let [parsed, setParsed] = React.useState<ImportParseResult | null>(null);
	let [plan, setPlan] = React.useState<BulkChangePlan | null>(null);
	let [undoPlan, setUndoPlan] = React.useState<BulkChangePlan | null>(null);
	let [excluded, setExcluded] = React.useState<{ [id: string]: boolean }>({});
	let [progress, setProgress] = React.useState<BulkRunProgress | null>(null);
	let [result, setResult] = React.useState<BulkRunResult | null>(null);
	let [undoResult, setUndoResult] = React.useState<BulkRunResult | null>(null);
	let [running, setRunning] = React.useState(false);
	let [rowStatus, setRowStatus] = React.useState<{ [id: string]: RowRunStatus }>({});
	let [rowErrors, setRowErrors] = React.useState<{ [id: string]: string }>({});
	let [readError, setReadError] = React.useState('');

	let cancelRef = React.useRef(false);
	let inputRef = React.useRef<HTMLInputElement>(null);

	React.useEffect(() => {
		if (!open) {
			setContext(null);
			return;
		}

		setContext(getContext());
		setStep('choose');
		setFileName('');
		setFileText('');
		setMissingAction('leave');
		setParsed(null);
		setPlan(null);
		setUndoPlan(null);
		setExcluded({});
		setProgress(null);
		setResult(null);
		setUndoResult(null);
		setRunning(false);
		setRowStatus({});
		setRowErrors({});
		setReadError('');
		cancelRef.current = false;
		// getContext is stable for the lifetime of the dialog's owner.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	let includedCount = plan ? selectableRows(plan).filter((row) => !excluded[row.id]).length : 0;

	async function onFileChosen(event: React.ChangeEvent<HTMLInputElement>) {
		let file = event.target.files?.[0];

		// Reset so choosing the same file again still fires a change event.
		event.target.value = '';

		if (!file) {
			return;
		}

		try {
			let text = await file.text();
			setFileName(file.name);
			setFileText(text);
			setReadError('');
		} catch (error: any) {
			setReadError(error?.message ?? 'Could not read that file');
		}
	}

	function buildPreview() {
		if (!fileText || !context) {
			return;
		}

		let { headers, records } = rowsToObjects(parseCsv(fileText));
		let outcome = buildImportPlan(context.records, records, headers, missingAction);

		setParsed(outcome);
		setPlan(outcome.plan);
		setUndoPlan(null);
		setExcluded({});

		if (outcome.plan) {
			setStep('preview');
		}
	}

	function trackingOptions(writes: { id: string }[]) {
		let initial: { [id: string]: RowRunStatus } = {};
		writes.forEach((write) => {
			initial[write.id] = 'pending';
		});
		setRowStatus(initial);
		setRowErrors({});

		function mark(ids: string[], status: RowRunStatus) {
			setRowStatus((current) => {
				let next = { ...current };
				ids.forEach((id) => {
					next[id] = status;
				});
				return next;
			});
		}

		return {
			onChunkStart: (ids: string[]) => mark(ids, 'writing'),
			onChunkRetry: (ids: string[]) => mark(ids, 'retrying'),
			onChunkSettled: (succeeded: string[], failed: { id: string; error: string }[]) => {
				mark(succeeded, 'done');
				mark(
					failed.map((failure) => failure.id),
					'failed'
				);
				setRowErrors((current) => {
					let next = { ...current };
					failed.forEach((failure) => {
						next[failure.id] = failure.error;
					});
					return next;
				});
			},
		};
	}

	async function apply() {
		if (!plan || !context?.model) {
			return;
		}

		let writes = planToWrites(plan, excluded);

		if (!writes.length) {
			return;
		}

		cancelRef.current = false;
		setRunning(true);
		setStep('run');
		setProgress({ completed: 0, total: writes.length, elapsedMs: 0, etaMs: estimateDurationMs(writes.length) });

		let runResult = await runBulkWrites(context.model, writes, {
			onProgress: setProgress,
			isCancelled: () => cancelRef.current,
			...trackingOptions(writes),
		});

		logEvent('bulkImport', {
			fileName,
			missingAction,
			attempted: writes.length,
			succeeded: runResult.succeeded.length,
			failed: runResult.failed.length,
			cancelled: runResult.cancelled,
		});

		setResult(runResult);
		setRunning(false);
		setStep('result');
		onApplied();
	}

	async function undo() {
		if (!plan || !result || !context?.model) {
			return;
		}

		let writes = planToUndoWrites(plan, result.succeeded);

		if (!writes.length) {
			return;
		}

		cancelRef.current = false;
		setRunning(true);
		setUndoPlan(invertPlan(plan, result.succeeded));
		setProgress({ completed: 0, total: writes.length, elapsedMs: 0, etaMs: estimateDurationMs(writes.length) });
		setStep('run');

		let runResult = await runBulkWrites(context.model, writes, {
			onProgress: setProgress,
			isCancelled: () => cancelRef.current,
			...trackingOptions(writes),
		});

		logEvent('bulkImportUndo', {
			fileName,
			attempted: writes.length,
			succeeded: runResult.succeeded.length,
			failed: runResult.failed.length,
		});

		setUndoResult(runResult);
		setRunning(false);
		setStep('result');
		onApplied();
	}

	function toggleRow(id: string) {
		setExcluded((current) => ({ ...current, [id]: !current[id] }));
	}

	function setAllRows(include: boolean) {
		if (!plan) {
			return;
		}

		let next: { [id: string]: boolean } = {};

		if (!include) {
			selectableRows(plan).forEach((row) => {
				next[row.id] = true;
			});
		}

		setExcluded(next);
	}

	function renderLiveTable() {
		let shown = undoPlan ?? plan;

		if (!shown) {
			return null;
		}

		return (
			<Box className={classes.table} mt={2}>
				<PreviewTable rows={shown.rows} excluded={excluded} onToggle={() => {}} statuses={rowStatus} errors={rowErrors} />
			</Box>
		);
	}

	function renderChoose() {
		return (
			<>
				<Alert severity="info" className={classes.section}>
					<AlertTitle>Import updates existing records only</AlertTitle>
					Rows are matched to records by <strong>{ID_COLUMN}</strong> or <strong>{CARD_COLUMN}</strong>. Only these
					columns are read back in: <strong>{editableColumns.map((column) => column.header).join(', ')}</strong>.
					Everything else in the file is there for context and is ignored. A blank cell leaves that field as it
					is, so a partly filled sheet cannot wipe data. Nothing is written until you have seen the preview.
				</Alert>

				<Box className={classes.section}>
					<Button variant="contained" color="primary" onClick={() => inputRef.current?.click()}>
						Choose CSV file
					</Button>
					{/* The app's CSS sets a display on inputs, which beats the weak UA style for
					    the hidden attribute, so the native control is hidden inline instead. */}
					<input ref={inputRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={onFileChosen} />
					{fileName ? (
						<Typography variant="body2" component="span" className={classes.fileName}>
							{fileName}
						</Typography>
					) : null}
				</Box>

				<FormControl fullWidth className={classes.section}>
					<InputLabel>Records not listed in the file</InputLabel>
					<Select
						value={missingAction}
						onChange={(event) => setMissingAction(event.target.value as ImportMissingAction)}
						MenuProps={overlayProps}
					>
						<MenuItem value="leave">Leave them untouched</MenuItem>
						<MenuItem value="softDelete">Move them to deleted</MenuItem>
					</Select>
				</FormControl>

				{readError ? <Alert severity="error">{readError}</Alert> : null}
				{parsed?.error ? <Alert severity="error">{parsed.error}</Alert> : null}
			</>
		);
	}

	function renderPreview() {
		if (!plan || !parsed) {
			return null;
		}

		return (
			<>
				<Typography variant="subtitle1" gutterBottom>
					{fileName}
				</Typography>

				<Box className={classes.counts}>
					<Chip color="primary" label={`${includedCount} will change`} />
					{plan.unchangedCount > 0 && <Chip variant="outlined" label={`${plan.unchangedCount} unchanged`} />}
					{parsed.notFoundCount > 0 && <Chip variant="outlined" label={`${parsed.notFoundCount} not found`} />}
					{parsed.missingFromFileCount > 0 && (
						<Chip variant="outlined" label={`${parsed.missingFromFileCount} not in the file`} />
					)}
				</Box>

				<Typography variant="body2" color="textSecondary" gutterBottom>
					{parsed.matchedCount} of {parsed.matchedCount + parsed.notFoundCount} rows matched a record.{' '}
					{parsed.missingFromFileCount > 0
						? missingAction === 'softDelete'
							? `${parsed.missingFromFileCount} records are not in the file and will be moved to deleted.`
							: `${parsed.missingFromFileCount} records are not in the file and stay as they are.`
						: ''}{' '}
					{describeDuration(includedCount)}
				</Typography>

				{parsed.unknownHeaders.length > 0 && (
					<Alert severity="warning" className={classes.section}>
						These columns were not recognised and are ignored: {parsed.unknownHeaders.join(', ')}
					</Alert>
				)}

				<Box mb={1}>
					<Button size="small" onClick={() => setAllRows(true)}>
						Include all
					</Button>
					<Button size="small" onClick={() => setAllRows(false)}>
						Exclude all
					</Button>
				</Box>

				<Box className={classes.table}>
					<PreviewTable rows={plan.rows} excluded={excluded} onToggle={toggleRow} />
				</Box>
			</>
		);
	}

	function renderResult() {
		let active = undoResult ?? result;

		if (!active) {
			return null;
		}

		let isUndo = !!undoResult;

		return (
			<>
				<RunResultAlert
					result={active}
					isUndo={isUndo}
					skippedCount={plan ? plan.unchangedCount + plan.blockedCount : 0}
					completeTitle="Import complete"
				/>

				{!isUndo && plan && isUndoable(plan) && result && result.succeeded.length > 0 && (
					<Alert severity="info" style={{ marginTop: 16 }}>
						<AlertTitle>Changed your mind?</AlertTitle>
						Undo writes the previous values back. Any edits made by someone else in the meantime would be
						overwritten.
					</Alert>
				)}

				{renderLiveTable()}
			</>
		);
	}

	let activeStepIndex = step === 'choose' ? 0 : step === 'preview' ? 1 : 2;

	return (
		<Dialog
			open={open}
			onClose={running ? undefined : onClose}
			maxWidth="lg"
			fullWidth
			disableBackdropClick={running}
			disableEscapeKeyDown={running}
			style={{ zIndex: DIALOG_Z_INDEX }}
		>
			<DialogTitle>Import records</DialogTitle>
			<DialogContent dividers>
				<Stepper activeStep={activeStepIndex} alternativeLabel>
					{STEPS.map((label) => (
						<Step key={label}>
							<StepLabel>{label}</StepLabel>
						</Step>
					))}
				</Stepper>

				{step === 'choose' && renderChoose()}
				{step === 'preview' && renderPreview()}
				{step === 'run' && (
					<Box py={1}>
						<RunProgressView
							title={undoPlan ? 'Undoing the import…' : 'Applying the import…'}
							progress={progress}
							cancelling={cancelRef.current}
						/>
						{renderLiveTable()}
					</Box>
				)}
				{step === 'result' && renderResult()}
			</DialogContent>
			<DialogActions>
				{step === 'choose' && (
					<>
						<Button onClick={onClose}>Cancel</Button>
						<Button color="primary" variant="contained" disabled={!fileText} onClick={buildPreview}>
							Preview changes
						</Button>
					</>
				)}

				{step === 'preview' && (
					<>
						<Button onClick={() => setStep('choose')}>Back</Button>
						<Button color="primary" variant="contained" disabled={!includedCount} onClick={apply}>
							Apply to {includedCount} records
						</Button>
					</>
				)}

				{step === 'run' && (
					<Button
						color="secondary"
						disabled={cancelRef.current}
						onClick={() => {
							cancelRef.current = true;
							setProgress((current) => (current ? { ...current } : current));
						}}
					>
						Cancel
					</Button>
				)}

				{step === 'result' && (
					<>
						{!undoResult && plan && isUndoable(plan) && !!result?.succeeded.length && (
							<Button color="secondary" disabled={running} onClick={undo}>
								Undo
							</Button>
						)}
						<Button color="primary" variant="contained" onClick={onClose}>
							Close
						</Button>
					</>
				)}
			</DialogActions>
		</Dialog>
	);
}

export default ImportDialog;
